/**
 * /calendar — week/month view + new-event dialog (UC-10.06).
 *
 * Search params (URL-persisted, validated with zod):
 * - `view`  → "week" | "month" (default "week")
 * - `date`  → YYYY-MM-DD anchor (default = today)
 *
 * Layout follows the Stitch wireframe in
 * `doc/design/dashboard/screens/05_calendar.html`.
 */

import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { z } from "zod";
import { CalendarWeekGrid } from "@/components/calendar/CalendarWeekGrid";
import { CalendarMonthGrid } from "@/components/calendar/CalendarMonthGrid";
import { EventDetailPanel } from "@/components/calendar/EventDetailPanel";
import { DayEventsPanel } from "@/components/calendar/DayEventsPanel";
import {
  NewEventDialog,
  type SubmittedEventDraft,
} from "@/components/calendar/NewEventDialog";
import { CreateEventConfirmDialog } from "@/components/calendar/CreateEventConfirmDialog";
import { useWeekEvents } from "@/hooks/useWeekEvents";
import { useMonthEvents } from "@/hooks/useMonthEvents";
import { useCreateEvent } from "@/hooks/useCreateEvent";
import {
  addMonths,
  addWeeks,
  formatMonthYear,
  formatWeekRange,
  getISOWeekNumber,
  isSameDay,
  parseISODate,
  startOfMonth,
  startOfWeekMonday,
  toISODate,
} from "@/lib/calendar-date-utils";
import type { CalendarEvent } from "@/lib/calendar-types";
import { cn } from "@/lib/utils";

const searchSchema = z.object({
  view: z.enum(["week", "month"]).optional(),
  date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});

type CalendarSearch = z.infer<typeof searchSchema>;

function CalendarPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const view = search.view ?? "week";
  const anchor = search.date ? parseISODate(search.date) : new Date();

  const weekStart = startOfWeekMonday(anchor);
  const monthStart = startOfMonth(anchor);

  const { items: weekEvents, isLoading: weekLoading } = useWeekEvents(weekStart);
  const { items: monthEvents, isLoading: monthLoading } = useMonthEvents(monthStart);

  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [isNewOpen, setIsNewOpen] = useState(false);
  const [pendingDraft, setPendingDraft] = useState<SubmittedEventDraft | null>(null);
  const createMutation = useCreateEvent();

  function setSearch(next: CalendarSearch) {
    void navigate({ search: next });
  }

  function shift(direction: 1 | -1) {
    if (view === "week") {
      const d = addWeeks(weekStart, direction);
      setSearch({ view, date: toISODate(d) });
    } else {
      const d = addMonths(monthStart, direction);
      setSearch({ view, date: toISODate(d) });
    }
  }

  function goToday() {
    setSearch({ view, date: toISODate(new Date()) });
  }

  const heroTitle = useMemo(
    () => formatMonthYear(view === "week" ? weekStart : monthStart),
    [view, weekStart, monthStart],
  );
  const heroSubtitle = useMemo(() => {
    if (view === "week") {
      return `Semana ${getISOWeekNumber(weekStart)} · ${formatWeekRange(weekStart)}`;
    }
    return formatMonthYear(monthStart);
  }, [view, weekStart, monthStart]);

  const selectedDayEvents = useMemo(
    () =>
      selectedDay
        ? monthEvents.filter((ev) =>
            isSameDay(new Date(ev.start_time), selectedDay),
          )
        : [],
    [selectedDay, monthEvents],
  );

  function handleConfirm() {
    if (!pendingDraft) return;
    createMutation.mutate(
      {
        title: pendingDraft.title,
        start_time: new Date(pendingDraft.start).toISOString(),
        end_time: new Date(pendingDraft.end).toISOString(),
        location: pendingDraft.location || undefined,
        description: pendingDraft.description || undefined,
        calendar_id: pendingDraft.calendar_id,
        calendar_name: pendingDraft.calendar_name,
        attendees: pendingDraft.attendees,
      },
      {
        onSuccess: () => {
          setPendingDraft(null);
          setIsNewOpen(false);
        },
      },
    );
  }

  return (
    <section className="flex flex-col gap-4">
      {/* Hero v2. */}
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-fg">
            Agenda · {heroTitle}
          </h1>
          <p className="text-sm text-fg-secondary mt-1 tabular">
            {heroSubtitle}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={goToday}
            className="h-8 px-3 rounded-md text-[13px] font-medium text-text-secondary hover:text-text-primary hover:bg-bg-elevated"
          >
            Hoy
          </button>
          <div className="flex items-center bg-bg-surface border border-subtle rounded-md">
            <button
              type="button"
              onClick={() => shift(-1)}
              aria-label="Anterior"
              className="h-8 w-8 flex items-center justify-center text-text-secondary hover:text-text-primary hover:bg-bg-elevated rounded-l-md"
            >
              <ChevronLeft className="h-[18px] w-[18px] stroke-[1.5]" aria-hidden />
            </button>
            <div className="h-4 w-px bg-subtle" aria-hidden />
            <button
              type="button"
              onClick={() => shift(1)}
              aria-label="Siguiente"
              className="h-8 w-8 flex items-center justify-center text-text-secondary hover:text-text-primary hover:bg-bg-elevated rounded-r-md"
            >
              <ChevronRight className="h-[18px] w-[18px] stroke-[1.5]" aria-hidden />
            </button>
          </div>

          {/* TabsList toggle pill. */}
          <div
            role="tablist"
            aria-label="Vista de calendario"
            className="inline-flex items-center bg-bg-elevated border border-subtle rounded-md p-0.5"
          >
            <ToggleTab
              active={view === "week"}
              onClick={() => setSearch({ view: "week", date: search.date })}
              label="Semana"
            />
            <ToggleTab
              active={view === "month"}
              onClick={() => setSearch({ view: "month", date: search.date })}
              label="Mes"
            />
          </div>

          <button
            type="button"
            onClick={() => setIsNewOpen(true)}
            data-testid="new-event-trigger"
            className={cn(
              "h-8 px-3 rounded-md text-sm font-medium",
              "bg-accent text-white hover:bg-accent-strong",
              "transition-colors duration-fast ease-out",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
              "inline-flex items-center gap-1.5",
            )}
          >
            <Plus className="size-4" strokeWidth={1.5} aria-hidden />
            Nuevo evento
          </button>
        </div>
      </header>

      {/* Grid. */}
      {view === "week" ? (
        weekLoading ? (
          <CalendarGridSkeleton rows={6} />
        ) : (
          <CalendarWeekGrid
            weekStart={weekStart}
            events={weekEvents}
            onEventClick={(ev) => setSelectedEvent(ev)}
          />
        )
      ) : monthLoading ? (
        <CalendarGridSkeleton rows={6} />
      ) : (
        <CalendarMonthGrid
          monthStart={monthStart}
          events={monthEvents}
          onEventClick={(ev) => setSelectedEvent(ev)}
          onDayClick={(day) => setSelectedDay(day)}
        />
      )}

      {/* Single-event detail panel. */}
      <EventDetailPanel
        event={selectedEvent}
        open={selectedEvent !== null}
        onClose={() => setSelectedEvent(null)}
      />

      {/* Day events list panel (month view → click a day). */}
      <DayEventsPanel
        day={selectedDay}
        events={selectedDayEvents}
        open={selectedDay !== null}
        onClose={() => setSelectedDay(null)}
        onEventClick={(ev) => {
          setSelectedDay(null);
          setSelectedEvent(ev);
        }}
      />

      {/* Create flow. */}
      <NewEventDialog
        open={isNewOpen}
        onClose={() => setIsNewOpen(false)}
        onRequestConfirm={(d) => setPendingDraft(d)}
      />
      <CreateEventConfirmDialog
        open={pendingDraft !== null}
        preview={
          pendingDraft
            ? {
                title: pendingDraft.title,
                start: new Date(pendingDraft.start),
                end: new Date(pendingDraft.end),
                calendarName: pendingDraft.calendar_name,
              }
            : null
        }
        isPending={createMutation.isPending}
        onCancel={() => setPendingDraft(null)}
        onConfirm={handleConfirm}
      />
    </section>
  );
}

/**
 * CalendarGridSkeleton — placeholder shown while week/month events load, so the
 * grid doesn't pop in abruptly. Mirrors the bordered-surface shell of both
 * grids with `rows × 7` pulsing cells.
 */
function CalendarGridSkeleton({ rows }: { rows: number }) {
  return (
    <div
      data-testid="calendar-grid-skeleton"
      aria-busy="true"
      className="bg-bg-surface border border-subtle rounded-lg overflow-hidden"
    >
      <div className="grid grid-cols-7 border-b border-subtle">
        {Array.from({ length: 7 }).map((_, i) => (
          <div
            key={i}
            className={cn("h-8", i < 6 && "border-r border-subtle")}
          />
        ))}
      </div>
      <div className="grid grid-cols-7">
        {Array.from({ length: rows * 7 }).map((_, i) => (
          <div
            key={i}
            className={cn(
              "h-[116px] p-1.5",
              i % 7 < 6 && "border-r border-subtle",
              i < (rows - 1) * 7 && "border-b border-subtle",
            )}
          >
            <div className="h-4 w-6 rounded bg-subtle animate-pulse" />
          </div>
        ))}
      </div>
    </div>
  );
}

interface ToggleTabProps {
  active: boolean;
  onClick: () => void;
  label: string;
}

function ToggleTab({ active, onClick, label }: ToggleTabProps) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "h-7 px-3 rounded text-[12px] font-medium transition-colors duration-fast",
        active
          ? "bg-bg-surface text-text-primary shadow-sm"
          : "text-text-secondary hover:text-text-primary",
      )}
    >
      {label}
    </button>
  );
}

export const Route = createFileRoute("/_authenticated/calendar")({
  validateSearch: searchSchema,
  component: CalendarPage,
});
